import {
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { FormInstance, message, Row, Select, Space, Tooltip } from 'antd';
import { BaseOptionType } from 'antd/lib/select';
import confirm from 'antd/lib/modal/confirm';
import { debounce, isEmpty } from 'lodash';
import moment, { Moment } from 'moment';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Collapse,
  InnerCollapse,
  Drawer,
  Form,
  FormItemDatePicker,
  FormItemInputNumber,
  FormItemRadioGroup,
  FormItemSelect,
  GenericDrawerHeader,
  Modal,
  ModalBlockNavigation,
  Button,
} from '../../components/index';
import { useCollapsePanels } from '../../hooks';
import {
  useGetOperatorsGroupbyManifestAndOperatorCompanyQuery,
  useLazyGetMerchantsCeGroupbyNcmItemsQuery,
} from '../../services/ncmItemApi';
import {
  useCreateOperationMutation,
  useDeleteOperationMutation,
  useGetOperationalDivergentVehiclesQuery,
  useUpdateOperationMutation,
  useSendDivergentVehicleMailMutation,
} from '../../services/operationApi';
import { useGetWarehousingDivergentVehiclesQuery } from '../../services/warehousingVehiclesApi';
import { useGetSystemParamByNameQuery } from '../../services/systemParamsApi';
import { useGetPortsQuery } from '../../services/vesselApi';
import { useGetCompaniesQuery } from '../../services/companyApi';
import {
  NcmItemFilters,
  OperationalOperationType,
  OperationProductType,
  PortType,
  Stopover,
} from '../../types';
import {
  formatOperationToForm,
  formatOperationToSave,
} from '../../utils/formatters';
import { booleanList, cargoNatureList } from '../../utils/lists';
import { isNullOrUndefined, removeSpecialCharacters } from '../../utils/utils';
import { LoadingDataCollapse } from '../../pages/private/agentArea/loadingData/loadingDataCollapse/LoadingDataCollapse';
import {
  convertToCeMerchantGroupbyNcmItemType,
  groupNCMItemByOpProduct,
} from './formatters';
import { ProvisionedCargoTable } from './provisionedCargoTable/ProvisionedCargoTable';
import { ChassisTable } from '../../pages/private/warehousingVehicles/chassisTable';
import { VehicleDivergencesAlert } from '../../pages/private/operatorArea/vehicleDivergenceAlert';

const { Panel } = Collapse;

const GENERAL_CARGO_ID = 1;
const CONTAINER_PRODUCT_GROUP_ID = 5;

const DOCKING_GOAL_SUPPORT = 40;
const CARGO_HANDLING = 38;

const GENERAL_CARGO_VEHICLE_TYPE_ID = 71;

const operationTypes: { [id: number]: string } = {
  20: 'SUPPORT',
  29: 'INTERMEDIATE_OPERATION',
  30: 'DEPARTURE',
  32: 'INTERNAL_TRANSFER',
  0: 'NONE',
};

const productGroupList = [
  { id: 0, name: 'Carga Geral Comum' },
  { id: 5, name: 'Container' },
  { id: 71, name: 'Veículos' },
];

type OperationalOperationDrawerProps = {
  dockingId?: number;
  addOperationToForm: (
    defaultValue?: any,
    insertIndex?: number | undefined
  ) => void;
  removeOperationFromForm: (
    defaultValue?: any,
    insertIndex?: number | undefined
  ) => void;
  isVisibleDrawer: boolean;
  setIsVisibleDrawer: Dispatch<SetStateAction<boolean>>;
  selectedOperation?: OperationalOperationType;
  selectedOperationIndex: number;
  setSelectedOperation: Dispatch<SetStateAction<OperationalOperationType>>;
  dockingForm: FormInstance;
  stopoverId: number;
  ncmIds: number[];
  setNcmIds: Dispatch<SetStateAction<number[]>>;
  ncmItemFilters: NcmItemFilters[];
  setNcmItemFilters: Dispatch<SetStateAction<NcmItemFilters[]>>;
};

type PreSubmitType = {
  operation: OperationalOperationType;
  oProduct: OperationProductType[];
  storagesWithoutEmail: string[];
  isLiquidBulk: boolean;
};

export function OperationalOperationDrawer(
  props: OperationalOperationDrawerProps
) {
  const {
    dockingId,
    addOperationToForm,
    removeOperationFromForm,
    isVisibleDrawer,
    setIsVisibleDrawer,
    selectedOperation,
    selectedOperationIndex,
    setSelectedOperation,
    dockingForm,
    stopoverId,
    ncmIds,
    setNcmIds,
    ncmItemFilters,
    setNcmItemFilters,
  } = props;

  const [form] = Form.useForm();

  const [portLoadingName, setPortLoadingName] = useState('');
  const [portUnloadingName, setPortUnloadingName] = useState('');
  const [storageCompanyName, setStorageCompanyName] = useState('');

  const [cargoOperationType, setCargoOperationType] = useState('');

  const generalCargoType = Form.useWatch(['product_group', 'id'], form);

  const [
    getMerchantsCeGroupbyNcmItems,
    { isLoading: isLoadingGetMerchantsCeGroupbyNcmItems },
  ] = useLazyGetMerchantsCeGroupbyNcmItemsQuery();

  const { data: operators, isFetching: isFetchingOperatorsData } =
    useGetOperatorsGroupbyManifestAndOperatorCompanyQuery(
      {
        stopover_id: stopoverId,
        ncm_item_assigned: true,
        cargo_assigned: false,
      },
      { skip: isNullOrUndefined(stopoverId), refetchOnMountOrArgChange: true }
    );

  const { data: operationTypesData, isLoading: isLoadingOperationTypesData } =
    useGetSystemParamByNameQuery({
      name: 'OPERATION_TYPE',
    });

  const { data: navigationTypesData, isLoading: isLoadingNavigationTypesData } =
    useGetSystemParamByNameQuery({
      name: 'NAVIGATION_TYPE',
    });

  const { data: storageCompanyData, isLoading: isLoadingStorageCompanyData } =
    useGetCompaniesQuery({
      company_type: 'STORAGE',
      name: storageCompanyName,
      page_size: 100,
    });

  const { data: portsLoadingData, isLoading: isLoadingPortsLoadingData } =
    useGetPortsQuery({ name: portLoadingName });

  const { data: portsUnloadingData, isLoading: isLoadingPortsUnloadingData } =
    useGetPortsQuery({ name: portUnloadingName });

  const {
    data: operationalDivergentVehicles,
    isFetching: isFetchingOperationalDivergentVehicles,
    isLoading: isLoadingOperationalDivergentVehicles,
  } = useGetOperationalDivergentVehiclesQuery({ stopoverId });

  const { data: warehousingDivergentVehicles } =
    useGetWarehousingDivergentVehiclesQuery({
      stopoverId,
    });

  const hasVehicleDivergence =
    (operationalDivergentVehicles &&
      operationalDivergentVehicles?.divergent_vehicles.length > 0) ||
    (warehousingDivergentVehicles &&
      warehousingDivergentVehicles?.divergent_vehicles.length > 0);

  const [sendDivergentVehicleMail] = useSendDivergentVehicleMailMutation();

  const [createOperation, { isLoading: isLoadingAddOperation }] =
    useCreateOperationMutation();

  const [updateOperation, { isLoading: isLoadingUpdateOperation }] =
    useUpdateOperationMutation();

  const [
    deleteOperation,
    {
      isSuccess: isSuccessDeleteOperation,
      isError: isErrorDeleteOperation,
      isLoading: isLoadingDeleteOperation,
    },
  ] = useDeleteOperationMutation();

  const [blockNavigate, setBlockNavigate] = useState(false);
  const [hasEdited, setHasEdited] = useState(false);
  const [natureOfCargo, setNatureOfCargo] = useState<string | undefined>(
    undefined
  );
  const [isCargoTypeManifested, setIsCargoTypeManifested] = useState(
    selectedOperation?.provisioned_cargos?.length === 0
  );
  const [wasPrefilledFromSummary, setWasPrefilledFromSummary] = useState(false);

  const [dataPreSubmit, setDataPreSubmit] = useState<PreSubmitType>(
    {} as PreSubmitType
  );

  const [isVisibleModalConfirmAssociate, setIsVisibleModalConfirmAssociate] =
    useState(false);

  const { onOpenPanel, openCollapsePanels } = useCollapsePanels(['cargos']);

  // Uma vez a operação criada com guindastes ou produtos, não deve ser possível alterar a natureza da carga/grupo de produto
  const disableEditNatureOfCargo = useMemo(() => {
    return (
      (selectedOperation?.items && selectedOperation?.items.length > 0) ||
      (selectedOperation?.operation_cranes &&
        selectedOperation?.operation_cranes?.length > 0)
    );
  }, [selectedOperation]);

  const notHasCranes = useMemo(() => {
    return (
      !selectedOperation?.operation_cranes ||
      (selectedOperation?.operation_cranes &&
        selectedOperation?.operation_cranes.length === 0)
    );
  }, [selectedOperation]);

  const [isContainerOperation, setIsContainerOperation] = useState(false);

  function onCloseDrawer() {
    setIsVisibleDrawer(false);
    setBlockNavigate(false);
    setHasEdited(false);
    setSelectedOperation({} as OperationalOperationType);
    setNatureOfCargo(undefined);
    form.resetFields();
    setIsVisibleModalConfirmAssociate(false);
    setIsContainerOperation(false);
    setCargoOperationType('');
    setPortLoadingName('');
    setPortUnloadingName('');
    setNcmItemFilters([]);
    setWasPrefilledFromSummary(false);
  }

  async function handleDeleteOperation() {
    confirm({
      title: 'Deseja realmente remover essa operação?',
      icon: <ExclamationCircleOutlined />,
      content: 'A operação removida não poderá ser recuperada',
      async onOk() {
        if (selectedOperation) {
          if (selectedOperation.id) {
            await deleteOperation(selectedOperation.id);
          } else {
            removeOperationFromForm(selectedOperationIndex);
            message.success('Operação removida com sucesso');
            onCloseDrawer();
          }
        }
      },
    });
  }

  function onChangeNatureOfCargo(cargoId: number | undefined) {
    const productGroup = form.getFieldValue(['product_group', 'id']);
    setIsContainerOperation(false);
    if (productGroup === CONTAINER_PRODUCT_GROUP_ID) {
      setIsContainerOperation(true);
    }
    if (cargoId) {
      if (cargoId !== GENERAL_CARGO_ID) {
        form.setFieldsValue({ product_group: null });
      }
      setNatureOfCargo(
        cargoNatureList.find((cargo) => cargo.id === cargoId)?.state_name
      );
    }
  }

  useEffect(() => {
    if (isSuccessDeleteOperation) {
      removeOperationFromForm(selectedOperationIndex);
      message.success('Operação removida com sucesso');
      onCloseDrawer();
    } else if (isErrorDeleteOperation) {
      message.error(
        'Ocorreu um erro ao excluir uma operação, se o erro persistir entre em contato com o suporte'
      );
    }
  }, [isSuccessDeleteOperation, isErrorDeleteOperation]);

  function formatPort(port: PortType | undefined) {
    const formatedPort = {
      ...port,
      bi_trigram: {
        value: port?.bi_trigram || '',
        label: `${port?.bi_trigram} - ${port?.name}` || '',
      },
    };
    return port ? formatedPort : '';
  }

  useEffect(() => {
    onChangeNatureOfCargo(selectedOperation?.nature_of_cargo?.id);
    setIsCargoTypeManifested(
      !(
        selectedOperation?.provisioned_cargos &&
        selectedOperation?.provisioned_cargos?.length > 0
      )
    );
    form.setFieldsValue(selectedOperation);

    let operationType = null;
    let navigationType = null;
    let operationDirection = null;
    let loadingPort = null;
    let unloadingPort = null;
    let storageCompany = null;
    if (
      selectedOperation?.provisioned_cargos &&
      selectedOperation?.provisioned_cargos.length > 0
    ) {
      operationType =
        selectedOperation?.provisioned_cargos[0].operation_type || null;
      navigationType =
        selectedOperation?.provisioned_cargos[0].navigation_type || null;
      operationDirection =
        selectedOperation?.provisioned_cargos[0].operation_direction || null;
      loadingPort = formatPort(
        selectedOperation?.provisioned_cargos[0].loading_port || null
      );
      unloadingPort = formatPort(
        selectedOperation?.provisioned_cargos[0].unloading_port || null
      );
      handleSelectCargoOperationType(
        selectedOperation?.provisioned_cargos[0].operation_type?.id || 0
      );
      storageCompany =
        selectedOperation?.provisioned_cargos[0].storage_company || null;
    }

    form.setFieldsValue({
      is_manifested_cargo: !(
        selectedOperation?.provisioned_cargos &&
        selectedOperation?.provisioned_cargos?.length > 0
      ),
      operation_type: operationType,
      navigation_type: navigationType,
      operation_direction: operationDirection,
      loading_port: loadingPort,
      unloading_port: unloadingPort,
      storage_company: storageCompany,
    });
    if (
      selectedOperation?.nature_of_cargo?.id === GENERAL_CARGO_ID &&
      selectedOperation.product_group === null
    ) {
      form.setFieldsValue({ product_group: { id: 0 } });
    }

    if (isVisibleDrawer && isEmpty(selectedOperation)) {
      const summaryNature = dockingForm.getFieldValue([
        'operation_summary',
        'nature_of_cargo',
      ]);
      if (summaryNature?.id) {
        form.setFieldsValue({ nature_of_cargo: { id: summaryNature.id } });
        onChangeNatureOfCargo(summaryNature.id);
        setWasPrefilledFromSummary(true);
      }

      const summaryExpectedStart = dockingForm.getFieldValue([
        'operation_summary',
        'expected_start',
      ]);
      const summaryExpectedFinish = dockingForm.getFieldValue([
        'operation_summary',
        'expected_finish',
      ]);
      if (
        isNullOrUndefined(form.getFieldValue('expected_operation_start')) &&
        isNullOrUndefined(form.getFieldValue('expected_operation_finish')) &&
        (!isNullOrUndefined(summaryExpectedStart) ||
          !isNullOrUndefined(summaryExpectedFinish))
      ) {
        form.setFieldsValue({
          expected_operation_start: summaryExpectedStart || null,
          expected_operation_finish: summaryExpectedFinish || null,
        });
      }
    }
  }, [isVisibleDrawer]);

  function validateOperationExpectedDates(
    start: string | Moment | null,
    end: string | Moment | null
  ) {
    if (start && end && start > end) {
      message.error(
        'Data de previsão de início da primeira operação não pode ser maior que previsão de fim da última operação',
        15
      );
      return false;
    }
    return true;
  }

  function handleAfterCreateUpdateCargo(
    operationAction: 'criada' | 'atualizada'
  ) {
    let msg = `Operação ${operationAction}. `;

    if (ncmItemFilters.length > 0) {
      msg +=
        'Uma notificação será criada quando todas as cargas estiverem atribuidas.';
    }
    message.success(msg);
  }
  function continueSubmit(
    values: any,
    formattedOperationProduct: OperationProductType[],
    isProvisionedCargo = false
  ) {
    // let operationProductToAdd = {
    //   ...formattedOperationProduct[0],
    // };
    // let operationProductToAdd = [
    //   ...formattedOperationProduct
    // ]

    const productsToAdd: any[] = [];
    values.docking_id = dockingId;

    // ajusta os ports para o formato esperado pela api
    values = {
      ...values,
      loading_port: values.loading_port?.bi_trigram?.value
        ? { bi_trigram: values.loading_port.bi_trigram.value }
        : values.loading_port,
      unloading_port: values.unloading_port?.bi_trigram?.value
        ? { bi_trigram: values.unloading_port.bi_trigram.value }
        : values.unloading_port,
    };

    // const existingProductItem = selectedOperation?.items?.find(
    //   (item) =>
    //     item.cargo_type?.name === operationProductToAdd.cargo_type?.name &&
    //     item.operator?.id === operationProductToAdd.operator?.id
    // );

    // checa se o produto já existe
    // if (!isProvisionedCargo && existingProductItem) {
    // caso exista atualiza com os dados dele
    // operationProductToAdd = {
    //   ...existingProductItem,
    //   id: existingProductItem.id,
    // };

    formattedOperationProduct.map((operationProduct) => {
      let operationProductToAdd = {
        ...operationProduct,
      };
      const existingProductItem = selectedOperation?.items?.find(
        (item) =>
          item.cargo_type?.name === operationProduct.cargo_type?.name &&
          item.operator?.id === operationProduct.operator?.id
      );

      if (!isProvisionedCargo && existingProductItem) {
        operationProductToAdd = {
          ...existingProductItem,
          id: existingProductItem.id,
        };
      }
      // itera sobre todos os manifestos para adicionar checando se ja existe
      operationProduct.operation_manifests?.forEach((om, index) => {
        // checa se o manifest code ja existe
        const existingManifest = existingProductItem?.operation_manifests?.find(
          (o) => o.manifest_code === om.manifest_code
        );
        // caso exista, apenas atualiza os dados do manifesto selecionado
        if (existingManifest) {
          let existingManifestIndex = -1;
          if (existingProductItem?.operation_manifests) {
            existingManifestIndex =
              existingProductItem.operation_manifests.findIndex(
                (manifest) => manifest.id === existingManifest.id
              );
          }
          if (!operationProductToAdd.operation_manifests) {
            operationProductToAdd.operation_manifests = [];
          }
          // itera sobre todos os merchantCe para adicionar checando se ja existe
          if (!operationProduct.operation_manifests)
            operationProduct.operation_manifests = [];
          operationProduct.operation_manifests[index].merchants_ce.forEach(
            (mce, indexMce) => {
              // checa se o mce já existe
              const existingMCe = existingManifest.merchants_ce.find(
                (mc) => mc.code === mce.code
              );

              if (!operationProductToAdd.operation_manifests)
                operationProductToAdd.operation_manifests = [];
              if (
                existingProductItem &&
                !existingProductItem?.operation_manifests
              )
                existingProductItem.operation_manifests = [];
              // caso exista, apenas atualiza os dados do merchantCe selecionado
              let existingMceIndex = -1;
              if (existingMCe)
                existingMceIndex =
                  existingManifest.merchants_ce?.indexOf(existingMCe);
              if (existingMCe) {
                // itera sobre todos os cargoStorage para adicionar checando se ja existe
                if (!operationProduct.operation_manifests)
                  operationProduct.operation_manifests = [];
                operationProduct.operation_manifests[index].merchants_ce[
                  indexMce
                ].storages?.forEach((cs) => {
                  if (operationProductToAdd.operation_manifests) {
                    const existingCargoStorage = existingMCe.storages?.find(
                      (c) => c.storage_company?.id === cs.storage_company?.id
                    );
                    // caso exista, apenas atualiza os dados do merchantCe selecionado e
                    // adiciona a mercadoria na listagem de cargos_storage
                    if (existingCargoStorage) {
                      const existingCSIndex =
                        existingMCe.storages?.indexOf(existingCargoStorage);
                      operationProductToAdd.operation_manifests[
                        existingManifestIndex
                      ].merchants_ce[existingMceIndex].storages[
                        existingCSIndex
                      ].cargos_storage = [
                        ...existingCargoStorage.cargos_storage,
                        ...cs.cargos_storage,
                      ];
                      // caso nao exista, apenas adiciona o cs ao mce já existente
                    } else if (
                      existingProductItem?.operation_manifests &&
                      existingProductItem?.operation_manifests[
                        existingManifestIndex
                      ]
                    ) {
                      // adiciona na listagem os novos csm e os já existentes
                      operationProductToAdd.operation_manifests[
                        existingManifestIndex
                      ].merchants_ce[existingMceIndex].storages = [
                        ...(existingProductItem?.operation_manifests[
                          existingManifestIndex
                        ].merchants_ce[existingMceIndex].storages || []),
                        cs,
                      ];
                    }
                  }
                });

                // caso nao exista, apenas adiciona o mce ao manifesto já existente
              } else if (
                existingProductItem &&
                existingProductItem.operation_manifests
              ) {
                operationProductToAdd.operation_manifests[
                  existingManifestIndex
                ].merchants_ce = [
                  ...(existingProductItem.operation_manifests[
                    existingManifestIndex
                  ].merchants_ce || []),
                  mce,
                ];
              }
            }
          );

          // caso nao exista, apenas adiciona o manifesto ao produto já existente
        } else {
          operationProductToAdd.operation_manifests = [
            ...(operationProduct?.operation_manifests || []),
          ];
        }
      });
      productsToAdd.push(operationProductToAdd);
    });
    // }
    const opToSave = formatOperationToSave(
      {
        ...values,
        docking_id: dockingId,
        items: isProvisionedCargo
          ? formattedOperationProduct
          : isEmpty(productsToAdd)
          ? []
          : productsToAdd,
        id: selectedOperation?.id,
      },
      ncmItemFilters,
      ncmIds
    );

    if (dockingId) {
      if (isEmpty(selectedOperation)) {
        createOperation(opToSave).then((operation) => {
          if ('data' in operation) {
            addOperationToForm(operation.data);
            handleAfterCreateUpdateCargo('criada');
            onCloseDrawer();
          }
        });
      } else {
        updateOperation(opToSave).then((updatedOperation) => {
          if ('data' in updatedOperation) {
            const operations = dockingForm.getFieldValue('operations');
            dockingForm.setFieldsValue({
              ...dockingForm,
              operations: operations.map(
                (operation: OperationalOperationType) => {
                  if (operation.id === updatedOperation.data?.id) {
                    return formatOperationToForm(updatedOperation.data);
                  }
                  return operation;
                }
              ),
            });
            handleAfterCreateUpdateCargo('atualizada');
            onCloseDrawer();
            setNcmIds([]);
          }
        });
      }

      setNcmItemFilters([]);
      setNcmIds([]);
    } else {
      values.nature_of_cargo = cargoNatureList.find(
        (item) => item.id === values.nature_of_cargo?.id
      );
      values.product_group =
        productGroupList.find((item) => item.id === values.product_group?.id) ||
        null;
      if (isEmpty(selectedOperation)) {
        addOperationToForm({
          ...values,
          docking_id: dockingId,
          items: formattedOperationProduct,
        });
      } else {
        const operations = dockingForm.getFieldValue('operations');
        dockingForm.setFieldsValue({
          ...dockingForm,
          operations: operations.map(
            (operation: OperationalOperationType, index: number) => {
              return index === selectedOperationIndex ? values : operation;
            }
          ),
        });
      }
      onCloseDrawer();
    }
  }

  async function handleFormSubmit() {
    form.validateFields().then(async (values: OperationalOperationType) => {
      if (
        values.expected_operation_start instanceof moment &&
        values.expected_operation_finish instanceof moment &&
        !validateOperationExpectedDates(
          values.expected_operation_start,
          values.expected_operation_finish
        )
      ) {
        return;
      }

      if (ncmItemFilters.length > 0 && ncmIds.length === 0) {
        // Apenas filtros (select all) — delega agrupamento ao backend
        const isLiquidBulk = values.nature_of_cargo?.id === 2;
        setDataPreSubmit({
          operation: values,
          oProduct: [],
          storagesWithoutEmail: [],
          isLiquidBulk,
        });
        setIsVisibleModalConfirmAssociate(true);
      } else if (ncmIds.length > 0) {
        // IDs individuais selecionados — mantém fetch e agrupamento no frontend
        const totalItemsFromFilters = ncmItemFilters.reduce(
          (sum, filter) => sum + (filter.total_items || 0),
          0
        );

        await getMerchantsCeGroupbyNcmItems({
          stopover_id: stopoverId,
          ncm_item_assigned: true,
          ncm_ids: ncmIds.join(','),
          page_size: totalItemsFromFilters || ncmIds.length || undefined,
        }).then((response) => {
          const isLiquidBulk = values.nature_of_cargo?.id === 2;
          const storagesWithoutEmail = new Set<string>();

          if (isLiquidBulk) {
            response.data?.results.forEach((item) => {
              if (
                item.storage_terminal &&
                isNullOrUndefined(item.storage_terminal?.email)
              ) {
                storagesWithoutEmail.add(item.storage_terminal.name);
              }
            });
          }

          const formattedOperationProduct = groupNCMItemByOpProduct(
            response.data?.results || [],
            selectedOperation?.id || 0
          );

          setDataPreSubmit({
            operation: values,
            oProduct: formattedOperationProduct,
            storagesWithoutEmail: Array.from(storagesWithoutEmail),
            isLiquidBulk,
          });
          setIsVisibleModalConfirmAssociate(true);
        });
      } else {
        // Provisões — sem carga manifestada selecionada
        const newsProvisionedCargos = values.provisioned_cargos
          ?.filter((item) => !item.id)
          .map((item) => ({
            ...item,
            loading_port: values.loading_port,
            unloading_port: values.unloading_port,
            storage_company: values.storage_company,
          }));

        const provisionedCargos = convertToCeMerchantGroupbyNcmItemType(
          newsProvisionedCargos
        );
        const formattedOperationProduct = groupNCMItemByOpProduct(
          provisionedCargos || [],
          selectedOperation?.id || 0,
          newsProvisionedCargos
        );
        continueSubmit(values, formattedOperationProduct, true);
      }
    });
  }

  function handleSelectCargoOperationType(e: number) {
    setCargoOperationType(operationTypes[e]);
  }

  const hasDirection = useMemo(() => {
    return (
      (dockingForm.getFieldValue(['docking_goal', 'id']) ===
        DOCKING_GOAL_SUPPORT &&
        cargoOperationType === 'SUPPORT') ||
      (dockingForm.getFieldValue(['docking_goal', 'id']) === CARGO_HANDLING &&
        (cargoOperationType === 'INTERMEDIATE_OPERATION' ||
          cargoOperationType === 'INTERNAL_TRANSFER' ||
          cargoOperationType === 'SUPPORT'))
    );
  }, [cargoOperationType]);

  function DivergentVehiclesHeader() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
        }}
      >
        <div>Chassis previstos no pátio não manifestados</div>
        <Tooltip title="Chassis previstos no pátio de veículos para esta viagem, mas não informados no manifesto de carga.">
          <QuestionCircleOutlined style={{ marginLeft: '8px' }} />
        </Tooltip>
      </div>
    );
  }

  function companyOptionRenderer(option: Record<string, any>) {
    return (
      <Select.Option key={option.id} value={option.id}>
        {option.name}
      </Select.Option>
    );
  }
  function filterOption(input: string, option: BaseOptionType) {
    const cleanLabel =
      removeSpecialCharacters(option?.props.children.toLowerCase(), /[./-]/g) ||
      '';
    const cleanInput =
      removeSpecialCharacters(input.toLowerCase(), /[./-]/g) || '';
    return cleanLabel.indexOf(cleanInput) >= 0;
  }

  return (
    <>
      <Modal
        title="Tem certeza que deseja associar estas cargas a esta operação?"
        visible={isVisibleModalConfirmAssociate}
        className="TOSPrimaryModal"
        okText="Continuar"
        cancelText="Voltar"
        width={640}
        okButtonProps={{
          loading: isLoadingUpdateOperation || isLoadingAddOperation,
        }}
        onOk={() =>
          continueSubmit(dataPreSubmit.operation, dataPreSubmit.oProduct)
        }
        onCancel={() => setIsVisibleModalConfirmAssociate(false)}
      >
        <div>
          <span>
            Ao associar as cargas a esta operação, não será possível editá-las
            posteriormente.
          </span>
          <br />
          <br />
          <span>
            {dataPreSubmit.storagesWithoutEmail &&
            dataPreSubmit.storagesWithoutEmail.length > 0
              ? 'Porém, os terminais de armazenamento envolvidos abaixo não serão notificados, pois não possuem emails cadastrados no sistema. Por favor, entre em contato com Suape para que cadastre essas informações.'
              : dataPreSubmit.isLiquidBulk
              ? 'Além disso, os terminais de armazenamento envolvidos serão notificados sobre esse armazenamento.'
              : ''}
          </span>
          <br />
          {dataPreSubmit.storagesWithoutEmail &&
            dataPreSubmit.storagesWithoutEmail.length > 0 && (
              <ul style={{ marginTop: '10px' }}>
                {dataPreSubmit.storagesWithoutEmail.map((company) => (
                  <li key={company}>{company}</li>
                ))}
              </ul>
            )}
        </div>
        {natureOfCargo === 'GENERAL_CARGO' &&
        generalCargoType === GENERAL_CARGO_VEHICLE_TYPE_ID &&
        hasVehicleDivergence ? (
          <VehicleDivergencesAlert
            onSendEmail={() => {
              sendDivergentVehicleMail({
                stopoverId,
              })
                .then(() => message.success('E-mail enviado com sucesso'))
                .catch(() => message.error('Falha ao enviar e-mail'));
            }}
            emailDisabled={!stopoverId}
          />
        ) : null}
      </Modal>
      <ModalBlockNavigation
        name="create_operation_by_agent"
        key="create_operation_by_agent"
        setHasEdited={setHasEdited}
        showModal={blockNavigate}
        setShowModal={setBlockNavigate}
        setBlockNavigate={setBlockNavigate}
        afterCloseModal={() => {
          onCloseDrawer();
          setNcmIds([]);
        }}
      />
      <Drawer
        visible={isVisibleDrawer}
        placement="right"
        width={900}
        closable={false}
        destroyOnClose
        onClose={() => {
          if (hasEdited) {
            setBlockNavigate(true);
          } else {
            onCloseDrawer();
            setNcmIds([]);
          }
        }}
      >
        <Form
          form={form}
          autoComplete="off"
          layout="vertical"
          name="create_operation_by_agent"
          id="create_operation_by_agent"
          onFinish={handleFormSubmit}
          onValuesChange={() => {
            setHasEdited(true);
          }}
        >
          <GenericDrawerHeader
            onBack={() => {
              if (hasEdited) {
                setBlockNavigate(true);
              } else {
                onCloseDrawer();
              }
            }}
            title={
              isEmpty(selectedOperation) ? (
                <span style={{ fontWeight: '700', fontSize: '16px' }}>
                  Nova operação
                </span>
              ) : (
                <span style={{ fontWeight: '700', fontSize: '16px' }}>
                  Operação {selectedOperation?.id}
                </span>
              )
            }
            onDelete={handleDeleteOperation}
            showBackButton
            formName="create_operation_by_agent"
            disabledSaveButton={
              isLoadingAddOperation ||
              isLoadingUpdateOperation ||
              isLoadingGetMerchantsCeGroupbyNcmItems ||
              isLoadingDeleteOperation
            }
            isLoadingSave={
              isLoadingAddOperation ||
              isLoadingUpdateOperation ||
              isLoadingGetMerchantsCeGroupbyNcmItems ||
              isLoadingDeleteOperation
            }
          />
          <div style={{ padding: '24px 16px' }}>
            <Row gutter={24} align="bottom">
              <FormItemRadioGroup
                label="Selecione o tipo da carga:"
                name="is_manifested_cargo"
                required
                onChange={(e) => setIsCargoTypeManifested(e.target.value)}
                options={[
                  {
                    label: 'Carga manifestada',
                    value: true,
                    disabled:
                      form.getFieldValue('provisioned_cargos')?.length > 0,
                  },
                  {
                    label: 'Provisões',
                    value: false,
                    disabled: ncmIds.length > 0,
                  },
                ]}
              />
            </Row>
            <Row gutter={20} align="top">
              <FormItemSelect
                colSpan={6}
                name={['nature_of_cargo', 'id']}
                label="Natureza da carga"
                tooltip={
                  wasPrefilledFromSummary
                    ? 'Valor herdado do resumo da atracação'
                    : undefined
                }
                dataList={cargoNatureList
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))}
                onSelect={(id) => {
                  onChangeNatureOfCargo(id);
                  setWasPrefilledFromSummary(false);
                }}
                onClear={() => setWasPrefilledFromSummary(false)}
                required
                disabled={disableEditNatureOfCargo}
              />
              {natureOfCargo === 'GENERAL_CARGO' ? (
                <>
                  <FormItemSelect
                    colSpan={12}
                    name={['product_group', 'id']}
                    label="Tipo de carga geral"
                    dataList={productGroupList
                      .slice()
                      .sort((a, b) => a.name.localeCompare(b.name))}
                    allowClear
                    disabled={disableEditNatureOfCargo}
                    onSelect={(id) => {
                      setIsContainerOperation(false);
                      if (id === CONTAINER_PRODUCT_GROUP_ID) {
                        setIsContainerOperation(true);
                      }
                    }}
                  />
                  <FormItemSelect
                    colSpan={6}
                    name="inspection"
                    label="Haverá inspeção?"
                    dataList={booleanList}
                  />
                </>
              ) : (
                natureOfCargo === 'LIQUID_BULK' && (
                  <>
                    <FormItemSelect
                      colSpan={6}
                      name="is_cargo_transshipment"
                      label="Transbordo?"
                      dataList={booleanList}
                    />
                    <FormItemInputNumber
                      colSpan={6}
                      name="flow_rate"
                      label="Vazão da operação"
                      addonAfter="m³/h"
                    />
                    <FormItemInputNumber
                      colSpan={6}
                      name="manifolds"
                      label="Quantidade de manifolds"
                    />
                  </>
                )
              )}
            </Row>
            {!form.getFieldValue('is_manifested_cargo') && (
              <>
                <Row gutter={20} align="top">
                  <FormItemSelect
                    colSpan={12}
                    name={['operation_type', 'id']}
                    label="Tipo da operação da carga"
                    notFoundContent="Não foi encontrado nenhum sentido da operação"
                    dataList={operationTypesData?.items}
                    isLoading={isLoadingOperationTypesData}
                    onSelect={handleSelectCargoOperationType}
                    required
                  />
                  {cargoOperationType === 'SUPPORT' && (
                    <FormItemSelect
                      colSpan={12}
                      name={['navigation_type', 'id']}
                      label="Tipo da navegação"
                      notFoundContent="Não foi encontrado nenhum sentido da operação"
                      dataList={navigationTypesData?.items.filter(
                        (item) =>
                          item.code === 'PORT_SUPPORT' ||
                          item.code === 'MARITIME_SUPPORT'
                      )}
                      isLoading={isLoadingNavigationTypesData}
                      required
                    />
                  )}
                </Row>
                {hasDirection && (
                  <Row gutter={20} align="top">
                    <FormItemSelect
                      colSpan={12}
                      required
                      name="operation_direction"
                      label="Sentido"
                      dataList={[
                        { id: 'LOADING', name: 'Embarque' },
                        { id: 'UNLOADING', name: 'Desembarque' },
                      ]}
                    />
                    <FormItemSelect
                      colSpan={12}
                      name={['storage_company', 'id']}
                      label="Local de armazenagem"
                      isLoading={isLoadingStorageCompanyData}
                      dataList={
                        storageCompanyData?.results
                          .slice()
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((company) => ({
                            ...company,
                            label: company.name,
                            value: company.id,
                          })) || []
                      }
                      showSearch
                      optionRenderer={companyOptionRenderer}
                      filterOption={filterOption}
                      optionFilterProp="label"
                    />
                  </Row>
                )}
                {cargoOperationType === 'SUPPORT' && (
                  <Row gutter={20} align="top">
                    <FormItemSelect
                      colSpan={12}
                      required
                      name={['loading_port', 'bi_trigram']}
                      label="Porto de embarque"
                      allowClear
                      showSearch
                      onSearch={debounce((e) => setPortLoadingName(e), 400)}
                      onClear={() =>
                        form.setFieldsValue({ port_boarding: null })
                      }
                      dataList={portsLoadingData?.results
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((port) => ({
                          ...port,
                          name: `${port.bi_trigram} - ${port.name}`,
                        }))}
                      isLoading={isLoadingPortsLoadingData}
                      notFoundContent="Nenhum porto encontrado para o filtro informado"
                    />
                    <FormItemSelect
                      colSpan={12}
                      required
                      name={['unloading_port', 'bi_trigram']}
                      label="Porto de desembarque"
                      allowClear
                      showSearch
                      onSearch={debounce((e) => setPortUnloadingName(e), 400)}
                      onClear={() =>
                        form.setFieldsValue({ port_Unloading: null })
                      }
                      dataList={portsUnloadingData?.results
                        .slice()
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((port) => ({
                          ...port,
                          name: `${port.bi_trigram} - ${port.name}`,
                        }))}
                      isLoading={isLoadingPortsUnloadingData}
                      notFoundContent="Nenhum porto encontrado para o filtro informado"
                    />
                  </Row>
                )}
              </>
            )}
            <Row gutter={20} align="top">
              <FormItemDatePicker
                colSpan={12}
                label="Previsão de início da primeira operação"
                name="expected_operation_start"
                minuteStep={15}
              />
              <FormItemDatePicker
                colSpan={12}
                label="Previsão de fim da última operação"
                name="expected_operation_finish"
                minuteStep={15}
              />
            </Row>
            {natureOfCargo === 'GENERAL_CARGO' &&
            generalCargoType === GENERAL_CARGO_VEHICLE_TYPE_ID ? (
              <Row justify="space-between" style={{ margin: '16px 0px' }}>
                <InnerCollapse
                  expandIconPosition="end"
                  style={{ width: '100%' }}
                >
                  <InnerCollapse.Panel
                    header={DivergentVehiclesHeader()}
                    key="stopover"
                  >
                    {isLoadingOperationalDivergentVehicles ||
                    isFetchingOperationalDivergentVehicles ? (
                      <div style={{ padding: '16px' }}>
                        Carregando veículos divergentes...
                      </div>
                    ) : operationalDivergentVehicles?.due_exists === false ? (
                      <div style={{ padding: '16px' }}>
                        Ainda não foi submetido um arquivo de previsão para esta
                        viagem.
                      </div>
                    ) : isEmpty(
                        operationalDivergentVehicles?.divergent_vehicles
                      ) ? (
                      <div style={{ padding: '16px' }}>
                        Não há divergências entre os veículos previstos e os
                        veículos manifestados.
                      </div>
                    ) : (
                      <div style={{ padding: '16px' }}>
                        <ChassisTable
                          totalChassis={
                            operationalDivergentVehicles?.divergent_vehicles
                              .length
                          }
                          stopover={{ id: stopoverId } as Stopover}
                          invoiceDueChassis={
                            operationalDivergentVehicles?.divergent_vehicles
                          }
                          isLoadingData={isLoadingOperationalDivergentVehicles}
                          onUpdate={() => {}}
                          type="DIVERGENT_MANIFESTED"
                          isWarehousingOperatorArea={false}
                          onChangePagination={(newPage) => newPage || 1}
                        />
                      </div>
                    )}
                  </InnerCollapse.Panel>
                </InnerCollapse>
              </Row>
            ) : null}
          </div>
          {notHasCranes && (
            <Collapse
              expandIconPosition="end"
              activeKey={openCollapsePanels}
              onChange={onOpenPanel}
            >
              <Panel forceRender header="Carga" key="cargos">
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {isCargoTypeManifested ? (
                    <div>
                      {!isContainerOperation && (
                        <div>
                          <span style={{ marginBottom: '10px' }}>
                            Selecione apenas as cargas que serão movimentadas
                            nesta operação:
                          </span>
                          <LoadingDataCollapse
                            stopoverId={stopoverId}
                            setNcmIdsToEdit={setNcmIds}
                            ncmIds={ncmIds}
                            setNcmItemFilters={setNcmItemFilters}
                            ncmItemFilters={ncmItemFilters}
                            operators={operators}
                            isFetchingOperatorsData={isFetchingOperatorsData}
                            cargosNotAssigned
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <ProvisionedCargoTable
                      hasAmount={
                        (dockingForm.getFieldValue(['docking_goal', 'id']) ===
                          DOCKING_GOAL_SUPPORT &&
                          cargoOperationType === 'SUPPORT') ||
                        cargoOperationType === 'DEPARTURE'
                      }
                      hasNcm={
                        (dockingForm.getFieldValue(['docking_goal', 'id']) ===
                          CARGO_HANDLING &&
                          (cargoOperationType === 'INTERMEDIATE_OPERATION' ||
                            cargoOperationType === 'INTERNAL_TRANSFER')) ||
                        cargoOperationType === 'DEPARTURE' ||
                        cargoOperationType === 'SUPPORT'
                      }
                      operation={
                        selectedOperation || ({} as OperationalOperationType)
                      }
                      ncmIsRequired={cargoOperationType !== 'SUPPORT'}
                    />
                  )}
                </div>
              </Panel>
            </Collapse>
          )}
        </Form>
      </Drawer>
    </>
  );
}
